import React, { FunctionComponent, createContext, useContext, useEffect } from 'react';
import Breadcrumb from '@modules/utilities/pageBuild/types/Breadcrumb';
import { TMenu, TMenuItem } from '@modules/utilities/pageBuild/menu';
import { Locale } from '@rbx/intl';
import { defaultLocale } from '@modules/localization/constants/translationResourceConstants';
import { useMediaQuery } from '@rbx/ui';
import { useQuery } from '@tanstack/react-query';
import { fetchIxpParameters, IxpLayers } from '@modules/clients/ixpExperiments';
import { useRobloxAuthentication } from '@rbx/auth';
import {
  getLocalEnableCourses,
  setLocalEnableCourses,
} from '@modules/learning/utils/localStorageSync';
import { showSecondaryNavigationMinBreakpoint } from '../constants/layoutConstants';

type AppConfigProviderProps = {
  breadcrumbs: Breadcrumb[];
  menuData: TMenu[];
  hideInPageNavigation: boolean;
  hideBreadcrumbs: boolean;
  contentDirFilePath?: string;
  isOpenSourced: boolean;
  blockSearchIndexing: boolean;
  locale: Locale;
  pageMaxWidth: number;
  machineTranslated: boolean;
  isLandingPage: boolean;
};

export type AppConfigProviderType = AppConfigProviderProps & {
  menu: TMenu | null;
  menuItem: TMenuItem | null;
  showSecondaryNavigation: boolean;
  enableNewLayout: boolean | null | undefined;
  isIXPFetched: boolean;
  enableCourses: boolean | null | undefined;
  enableEngineReferenceRevamp: boolean | null | undefined;
};

const AppConfigProviderContext = createContext<AppConfigProviderType>({
  breadcrumbs: [],
  menuData: [],
  menu: null,
  menuItem: null,
  hideInPageNavigation: false,
  hideBreadcrumbs: false,
  contentDirFilePath: '',
  isOpenSourced: false,
  blockSearchIndexing: false,
  locale: defaultLocale,
  showSecondaryNavigation: false,
  pageMaxWidth: 1280,
  machineTranslated: false,
  isLandingPage: false,
  enableNewLayout: false,
  isIXPFetched: false,
  enableCourses: false,
  enableEngineReferenceRevamp: false,
});
AppConfigProviderContext.displayName = 'AppConfig';

export function useAppConfig() {
  return useContext(AppConfigProviderContext);
}

const AppConfigProvider: FunctionComponent<
  React.PropsWithChildren<
    AppConfigProviderProps & {
      hideSecondaryNav: boolean;
    }
  >
> = ({
  breadcrumbs,
  menuData,
  hideInPageNavigation,
  hideSecondaryNav,
  hideBreadcrumbs,
  contentDirFilePath,
  isOpenSourced,
  blockSearchIndexing,
  locale,
  isLandingPage,
  pageMaxWidth,
  machineTranslated,
  children,
}) => {
  const menu = menuData.find((m) => m.selected) ?? null;
  const menuItem = menu?.items.find((m) => m.selected) ?? null;
  // TODO UnifiedTop: get the breakpoints from UnifiedTopNavigation provider
  const isSecondaryNavShowableScreen = useMediaQuery((theme) =>
    theme.breakpoints.up(showSecondaryNavigationMinBreakpoint),
  );
  const showSecondaryNavigation = !!menu && !hideSecondaryNav && isSecondaryNavShowableScreen;

  const { user: authenticatedUser } = useRobloxAuthentication();
  const userId = authenticatedUser?.id?.toString();

  const localEnableCourses = getLocalEnableCourses(userId);

  const { data, isFetched } = useQuery({
    queryKey: ['ixp', 'combined', IxpLayers.CreatorHubNavigationUser, IxpLayers.UserId],
    queryFn: async () => {
      const [navigationData, engineReferenceData, coursesData] = await Promise.all([
        fetchIxpParameters(IxpLayers.CreatorHubNavigationUser),
        fetchIxpParameters(IxpLayers.EngineReference),
        localEnableCourses
          ? Promise.resolve({ enableCourses: true })
          : fetchIxpParameters(IxpLayers.UserId),
      ]);
      return {
        navigation: navigationData,
        engineReference: engineReferenceData,
        courses: coursesData,
      };
    },
  });

  useEffect(() => {
    if (data?.courses?.enableCourses === true && userId && !localEnableCourses) {
      setLocalEnableCourses(true, userId);
    }
  }, [data?.courses?.enableCourses, userId, localEnableCourses]);

  return (
    <AppConfigProviderContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        breadcrumbs,
        menuData,
        menu,
        menuItem,
        hideInPageNavigation,
        hideBreadcrumbs,
        isOpenSourced,
        blockSearchIndexing,
        locale,
        contentDirFilePath,
        showSecondaryNavigation,
        isLandingPage,
        pageMaxWidth,
        machineTranslated,
        enableNewLayout: data?.navigation?.enableIAM2,
        isIXPFetched: isFetched,
        enableCourses: localEnableCourses || data?.courses?.enableCourses,
        enableEngineReferenceRevamp:
          data?.engineReference?.enableReferenceRevamp2025Q4 ||
          process.env.enableEngineReferenceRevamp,
      }}>
      {children}
    </AppConfigProviderContext.Provider>
  );
};

export default AppConfigProvider;
